import { Injectable, computed, inject, signal } from '@angular/core';

import { CustomerService } from './customer.service';
import { CustomerTenant } from './models';
import { hasPerm } from './permissions';

/**
 * Caches `GET /v1/customer/tenants/` — the source of truth for "which tenants
 * is this user a member of, with which permissions". The
 * tenant shell + guard read from this store so we don't hit the network on
 * every navigation. Refreshed once after login and on demand (e.g. after a
 * mutation that could change membership).
 */
@Injectable({ providedIn: 'root' })
export class TenantMembershipsStore {
  private customer = inject(CustomerService);

  private readonly _items = signal<CustomerTenant[] | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private inflight: Promise<CustomerTenant[]> | null = null;

  readonly items = this._items.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly count = computed(() => this._items()?.length ?? 0);

  /** Ensure the list is loaded. Re-uses an in-flight promise if one exists. */
  async ensureLoaded(): Promise<CustomerTenant[]> {
    if (this._items() !== null) return this._items()!;
    return this.reload();
  }

  /** Force a fresh load. */
  reload(): Promise<CustomerTenant[]> {
    if (this.inflight) return this.inflight;
    this._loading.set(true);
    this._error.set(null);
    this.inflight = this.customer
      .tenants()
      .then((list) => {
        this._items.set(list);
        return list;
      })
      .catch((e) => {
        this._error.set('Could not load your workspaces.');
        this._items.set([]);
        throw e;
      })
      .finally(() => {
        this._loading.set(false);
        this.inflight = null;
      });
    return this.inflight;
  }

  /** Drop the cache — e.g. after sign-out or membership mutation. */
  invalidate(): void {
    this._items.set(null);
    this._error.set(null);
  }

  getBySlug(slug: string): CustomerTenant | null {
    return this._items()?.find((t) => t.slug === slug) ?? null;
  }

  hasMembership(slug: string): boolean {
    return this.getBySlug(slug) !== null;
  }

  hasPerm(slug: string, perm: string): boolean {
    return hasPerm(this.getBySlug(slug), perm);
  }

}
